/* eslint-disable @typescript-eslint/no-explicit-any */
/*
 * Root Reducer
 * combine all reducers to create root reducer
 */

import { combineReducers, type Action } from "@reduxjs/toolkit";
import { persistReducer } from "redux-persist";
import Storage from "redux-persist/lib/storage";

import { bootstrapReducer } from "@app/modules/bootstrap";
import { commonReducer } from "@app/modules/common";
import { loginReducer, logout } from "@app/modules/auth";
import { carTypesReducer } from "@app/modules/home";
import { brandReducer } from "@app/modules/categories";
import { usedCarsReducer } from "@app/modules/product-list-page";
import { createOrderReducer } from "@app/modules/buy";

import { testDriveReducer } from "@app/modules/test-drive-process";
import { myProfileReducer } from "@app/modules/my-profile";
import { contactUsReducer } from "@app/modules/footer";
import { wishlistReducer } from "@app/modules/wishlist";
import { viewsReducer } from "@app/modules/product-detail";
import { sellCarReducer } from "@app/modules/sellCar";
import { myOrdersReducer } from "@app/modules/my-orders";
import { investorReducers } from "@app/modules/beAnInvestor";

/* -----[ persist configurations ]------ */
const rootPersistConfig = {
  key: "root",
  storage: Storage,
  whitelist: ["version", "commonReducer"],
};

const preserveAlways = ["bootstrapReducer"];

const stateVersion = 1; // Update it whenever change state structure of whitelist slice

const appReducer = combineReducers({
  bootstrapReducer,
  commonReducer,
  carTypesReducer,
  loginReducer,
  brandReducer,
  createOrderReducer,
  usedCarsReducer,
  testDriveReducer,
  myProfileReducer,
  contactUsReducer,
  wishlistReducer,
  viewsReducer,
  sellCarReducer,
  myOrdersReducer,
  investorReducers,
  version: (state = stateVersion) => state,
});

const rootReducer = (
  state: ReturnType<typeof appReducer>,
  action: Action<any>,
) => {
  if (action.type === logout.type) {
    const newState: any = Object.keys(state).reduce((acc, key) => {
      if (preserveAlways.includes(key)) {
        (acc as any)[key] = (state as any)[key];
      } else {
        (acc as any)[key] = undefined;
      }
      return acc;
    }, {});
    return appReducer(newState, action);
  }

  return appReducer(state, action);
};

const persistRootReducer = persistReducer(
  rootPersistConfig,
  (state, action) => {
    const migratedState = migrateState(state);
    return rootReducer(migratedState, action);
  },
);

const migrateState = (state: any) => {
  if (state !== undefined && state.version !== stateVersion) {
    return {
      ...state,
      version: stateVersion,
    };
  }

  return state;
};

export type RootState = ReturnType<typeof rootReducer>;

export { persistRootReducer };
